// frontend/src/components/shared/TvScreenPreview.jsx
// Réplica do player (player-agent/web: tv.html/tv.css/tv.js) no viewport CSS real das TVs
// (~540×960, ver diagnostics → viewport), reduzida com CSS transform. Os valores em px/vw do
// tv.css entram literalmente — altere aqui se mudar o player.
import React from "react";

const VW = 540, VH = 960;
const vw = (n) => VW * n / 100;
const clamp = (min, v, max) => Math.min(max, Math.max(min, v));

// Mídia do comunicado: renderiza <video> para MP4/WebM, <img> para imagens
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url || "");
const NoticeMedia = ({ src, alt, isVideo, ...props }) =>
  (isVideo ?? isVideoUrl(src))
    ? <video src={src} muted autoPlay loop playsInline {...props} />
    : <img src={src} alt={alt || ""} {...props} />;

// Texto do comunicado com scroll animado — igual ao comportamento do player real
// loop=false → scroll único até o fim (layout2/layout4)
// loop=true  → loop infinito: rola → pausa → volta → rola (layout3 assembleia)
function NoticeScroller({ html, style, loop = false }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = 0;
    const PAUSE = 2000;
    const SPEED = 80;
    let raf, timeoutId;
    function startLoop() {
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 2) return;
      const duration = (maxScroll / SPEED) * 1000;
      const startTime = performance.now();
      function animate(ts) {
        if (!ref.current) return;
        const progress = Math.min((ts - startTime) / duration, 1);
        el.scrollTop = maxScroll * progress;
        if (progress < 1) { raf = requestAnimationFrame(animate); }
        else {
          el.scrollTop = maxScroll;
          if (loop) {
            timeoutId = setTimeout(() => { el.scrollTop = 0; timeoutId = setTimeout(startLoop, PAUSE); }, PAUSE);
          }
        }
      }
      raf = requestAnimationFrame(animate);
    }
    timeoutId = setTimeout(startLoop, PAUSE);
    return () => { cancelAnimationFrame(raf); clearTimeout(timeoutId); };
  }, [html, loop]);
  return <div ref={ref} style={{ ...style, overflowY: "hidden" }} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function formatNoticeHtml(rawText) {
  if (!rawText) return "";

  let html = String(rawText).trim();
  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(html);

  if (!hasHtml) {
    html = html
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\n\n/g, "</p><p>")
      .replace(/\n/g, "<br/>");
    html = `<p>${html}</p>`;
  }

  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+=("[^"]*"|'[^']*')/gi, "")
    .replace(/<p><br\s*\/?><\/p>/gi, '<p style="margin:0 0 2px 0; min-height:1.2em;"><br/></p>')
    .replace(/<p>/gi, '<p style="margin:0 0 2px 0;">');
}

/**
 * Prévia 9:16 do que uma tela exibe.
 * @param width      largura total do aparelho (moldura incluída), em px
 * @param layoutType layout da tela (layout1 → layout2, como no player)
 * @param isAssembly convocação de assembleia ativa → layout3
 * @param mediaUrl / mediaType  mídia de campanha tocando (layout2)
 * @param notice     { title, text, imageUrl, mediaType? } do comunicado no ar (mediaType "VIDEO"
 *                   quando a URL não tem extensão, ex.: arquivo local blob:)
 * @param footerUrl  imagem do rodapé da tela
 * @param isOnline   mostra o selo AO VIVO / OFFLINE quando definido
 * @param estimated  mostra o selo "estimado"
 * @param mediaPlaceholder texto na área de mídia (layout2) quando não há mídia
 */
export default function TvScreenPreview({
  width = 162, layoutType, isAssembly = false, mediaUrl = null, mediaType = null,
  notice = null, footerUrl = null, isOnline, estimated = false, mediaPlaceholder = "Sem mídia disponível.",
}) {
  const layout = isAssembly ? "layout3"
    : (layoutType === "layout1" || !layoutType) ? "layout2"
    : layoutType;
  const isLayout2 = layout === "layout2";
  const isLayout3 = layout === "layout3";
  const isLayout4 = layout === "layout4";

  const mediaIsImage = !!mediaUrl && mediaType === "IMAGE";
  const noticeHasImage = !!(notice && notice.imageUrl);
  const noticeHasText = !!(notice && notice.text && notice.text.trim());
  const noticeImageOnly = noticeHasImage && !noticeHasText;

  // Device físico: tela 9:16 exata, igual à da TV
  const BEZEL = 5;
  const innerW = Math.max(60, Math.floor(width) - BEZEL * 2);
  const innerH = Math.round(innerW * VH / VW);
  const PS = innerW / VW;

  // Relógio (tv.js: updateClock) — "Ter. 13 de out."
  const now = new Date();
  const clockTime = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hour12: false }).format(now);
  const clockDate = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", weekday: "short", day: "numeric", month: "short" }).format(now)
    .replace(/^(\w+)[,.]?\s+/, (m, w) => `${w.charAt(0).toUpperCase()}${w.slice(1).toLowerCase()}. `)
    .replace(/(\w{3})$/, "$1.");
  const clockTheme = isLayout2
    ? { bg: "rgba(90,90,90,0.50)", border: "1px solid rgba(255,255,255,0.25)", shadow: "0 2px 10px rgba(0,0,0,0.25)", time: "#ffffff", timeShadow: "0 2px 6px rgba(0,0,0,0.45)", date: "#f0f0f0", dateShadow: "0 1px 3px rgba(0,0,0,0.4)" }
    : { bg: "rgba(255,255,255,0.55)", border: "1.5px solid rgba(0,0,0,0.18)", shadow: "none", time: "rgba(55,55,62,0.75)", timeShadow: "none", date: "rgba(75,75,83,0.65)", dateShadow: "none" };

  // Card do comunicado (.notices-area .notice-card + variações por layout/tipo)
  const cardStyle = {
    flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", boxSizing: "border-box",
    background: "#ffffff", position: "relative",
    ...(isLayout2
      ? { borderRadius: "37px 37px 0 0", marginTop: -30, boxShadow: "0 24px 64px rgba(0,0,0,0.25), 0 12px 32px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.12)" }
      : { borderRadius: 0, margin: 0, boxShadow: "none" }),
    ...(noticeImageOnly
      ? { padding: 0 }
      : { padding: "10px 40px 20px 40px", background: `linear-gradient(180deg, #ffffff 0%, ${isLayout3 ? "#edf2f7" : "#f3f5f8"} 100%)` }),
  };
  const titleStyle = {
    fontWeight: 700, fontSize: clamp(26, vw(4), 48), lineHeight: 1.15, letterSpacing: "-0.42px",
    margin: "0 0 6px 0", padding: "20px 0 0 0", color: isLayout3 ? "#0a2644" : "#0e2c4f",
    textTransform: "uppercase", textAlign: "center", flexShrink: 0,
  };
  const textStyle = {
    flex: 1, lineHeight: 1.45, color: isLayout3 ? "#164573" : "#123a67",
    fontSize: isLayout3 ? clamp(13, vw(1.8), 22) : clamp(16, vw(2.5), 30),
  };

  // Selos fora do palco virtual, proporcionais ao aparelho
  const badgeFont = Math.max(7, Math.round(innerW / 19));

  return (
    <div style={{
      width: innerW + BEZEL * 2, height: innerH + BEZEL * 2, backgroundColor: "#000",
      border: `${BEZEL}px solid #222`, borderRadius: 14, overflow: "hidden", boxSizing: "border-box",
      position: "relative", boxShadow: "0 8px 18px rgba(0,0,0,0.35)", flexShrink: 0,
    }}>
      {isOnline !== undefined && (
        <span style={{ position: "absolute", top: 6, left: 6, zIndex: 40, fontSize: badgeFont, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: isOnline ? "rgba(30,166,114,0.95)" : "rgba(40,40,46,0.85)", color: "#fff", letterSpacing: "0.04em" }}>
          {isOnline ? "AO VIVO" : "OFFLINE"}
        </span>
      )}
      {estimated && (
        <span style={{ position: "absolute", bottom: 6, left: 6, zIndex: 40, fontSize: badgeFont - 1, fontWeight: 600, padding: "1px 5px", borderRadius: 999, background: "rgba(180,83,9,0.16)", color: "#fde2c2", letterSpacing: "0.03em" }}>
          estimado
        </span>
      )}

      {/* Palco virtual no viewport da TV, depois reduzido */}
      <div style={{
        width: VW, height: VH, transform: `scale(${PS})`, transformOrigin: "top left",
        position: "absolute", top: 0, left: 0, display: "flex", flexDirection: "column", overflow: "hidden",
        background: isLayout3 ? "#ffffff" : "#000",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "antialiased", textAlign: "left",
      }}>
        {/* Relógio (.clock) */}
        <div style={{
          position: "absolute", top: 20, right: 30, zIndex: 1000, textAlign: "right", lineHeight: 1.2,
          background: clockTheme.bg, border: clockTheme.border, boxShadow: clockTheme.shadow,
          padding: "8px 14px", borderRadius: 12, pointerEvents: "none",
        }}>
          <span style={{ display: "block", fontSize: clamp(18, vw(3), 32), fontWeight: 700, lineHeight: 1, letterSpacing: "0.5px", color: clockTheme.time, textShadow: clockTheme.timeShadow }}>{clockTime}</span>
          <span style={{ display: "block", fontSize: clamp(6, vw(1.1), 10), fontWeight: 500, marginTop: 3, letterSpacing: "0.2px", whiteSpace: "nowrap", opacity: 0.8, color: clockTheme.date, textShadow: clockTheme.dateShadow }}>{clockDate}</span>
        </div>

        {/* .app: altura = tela − rodapé */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: (isLayout3 || isLayout4) ? "#ffffff" : "#000" }}>
          {/* .media-area: quadrado 100vw, só no layout2 */}
          {isLayout2 && (
            <div style={{ flex: "0 0 auto", width: VW, height: VW, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", zIndex: 50 }}>
              {mediaUrl ? (
                mediaIsImage
                  ? <img key={mediaUrl} src={mediaUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                  : <video key={mediaUrl} src={mediaUrl} autoPlay muted loop playsInline preload="auto" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
              ) : (
                <div style={{ color: "#8a8a8a", fontSize: 22, textAlign: "center", padding: 24, whiteSpace: "pre-line", lineHeight: 1.5 }}>{mediaPlaceholder}</div>
              )}
            </div>
          )}

          {/* .notices-area */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: "#ffffff", position: "relative", zIndex: 100 }}>
            {notice && (
              <div style={cardStyle}>
                {noticeImageOnly ? (
                  // Só mídia (imagem ou vídeo): sem título, preenche o card (object-fit: cover / ExoPlayer zoom)
                  <NoticeMedia src={notice.imageUrl} isVideo={notice.mediaType ? notice.mediaType === "VIDEO" : undefined} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                ) : (
                  <>
                    <div style={titleStyle}>{notice.title || "Comunicado"}</div>
                    {noticeHasImage ? (
                      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", alignItems: "stretch" }}>
                        <div style={{ flex: "0 0 25%", maxWidth: "25%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                          <NoticeMedia src={notice.imageUrl} isVideo={notice.mediaType ? notice.mediaType === "VIDEO" : undefined} alt="" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12, display: "block" }} />
                        </div>
                        <NoticeScroller html={formatNoticeHtml(notice.text)} loop={isLayout3} style={{ ...textStyle, paddingLeft: 40 }} />
                      </div>
                    ) : (
                      <NoticeScroller html={formatNoticeHtml(notice.text || "")} loop={isLayout3} style={textStyle} />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* .footer-bar: largura total, altura pela proporção da imagem */}
        {footerUrl && (
          <div style={{ flexShrink: 0, lineHeight: 0, fontSize: 0, position: "relative", zIndex: 9999 }}>
            <img src={footerUrl} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
          </div>
        )}
      </div>
    </div>
  );
}
